import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text, { TextVariant } from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import { TooltipProps } from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'
import React from 'react'

interface PlusButtonProps {
  onClick: () => void
  size?: 'lg' | 'md' | 'sm'
  label?: string
  icon?: string
  labelVariant?: TextVariant
  disabled?: boolean
  tooltip?: string
  tooltipProps?: TooltipProps
}

/**
 * @summary A button that adds a new entry
 * @returns {React.ReactNode}
 */
const PlusButton = ({
  onClick,
  size = 'lg',
  label = 'Add',
  icon = 'plus-circle',
  labelVariant = 'body2',
  disabled = false,
  tooltip = '',
  tooltipProps = {},
}: PlusButtonProps) => {
  const classes = useStyles({ disabled })

  return (
    <Tooltip message={tooltip ? tooltip : undefined} {...tooltipProps}>
      <div
        className={classes.addNewEntry}
        onClick={disabled ? undefined : onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            onClick()
            e.preventDefault()
          }
        }}
        aria-disabled={disabled}
      >
        <FontAwesomeIcon className={classes.plus} size={size}>
          {icon}
        </FontAwesomeIcon>
        <Text variant={labelVariant}>{label}</Text>
      </div>
    </Tooltip>
  )
}

interface useStylesProps {
  disabled: boolean
}

const useStyles = makeStyles<Theme, useStylesProps>((theme) => ({
  addNewEntry: {
    display: 'flex',
    alignItems: 'center',
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    marginRight: theme.spacing(2),
    color: ({ disabled }) =>
      disabled
        ? theme.components.button.primary.disabledBackground
        : theme.components.button.primary.color,
  },
  plus: {
    color: ({ disabled }) =>
      disabled
        ? theme.components.button.primary.disabledBackground
        : theme.components.button.primary.background,
    marginRight: theme.spacing(1),
    fontWeight: 900,
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
  },
}))

export default PlusButton
